import type { EnhancedDomain, Model, StackProjections } from 'jms-domain'
import { getCountertopWallStackId } from 'jms-domain'

import type { DomainState } from '@salsita-ai/platform-project-sdk/domain'

import { configuratorInterface } from '../../systems/configurator-interface.js'

/**
 * Calculate continuous countertop spaces based on bottom items
 * Replicates the logic from wall.ts $continuousCountertopSpaces
 */
export const calculateContinuousCountertopSpaces = (
  bottomItems: Array<{ x: number; $width: number; $hasWorkSurface: boolean }>
): Array<{ width: number; minX: number; maxX: number }> => {
  const countertopSpaces: Array<{ width: number; minX: number; maxX: number }> = []
  const sortedItems = [...bottomItems].sort((a, b) => a.x - b.x)

  if (sortedItems.length === 0) {
    return countertopSpaces
  }

  let continuousSpaceStartX: number | null = null
  let currentX: number | null = null

  for (let i = 0; i < sortedItems.length; i++) {
    const currentItem = sortedItems[i]!
    const itemStart = currentItem.x
    const itemEnd = currentItem.x + currentItem.$width

    // Skip items without work surface - they interrupt continuous spaces
    if (!currentItem.$hasWorkSurface) {
      // Close current continuous space if one exists
      if (continuousSpaceStartX !== null && currentX !== null) {
        countertopSpaces.push({
          width: currentX - continuousSpaceStartX,
          minX: continuousSpaceStartX,
          maxX: currentX,
        })
      }
      // Reset tracking
      continuousSpaceStartX = null
      currentX = null
      continue
    }

    // Item has work surface
    if (continuousSpaceStartX === null || currentX === null) {
      // Start a new continuous space
      continuousSpaceStartX = itemStart
      currentX = itemEnd
    } else if (currentX === itemStart) {
      // Item is continuous with previous - extend the space
      currentX = itemEnd
    } else {
      // There's a gap - close current space and start new one
      countertopSpaces.push({
        width: currentX - continuousSpaceStartX,
        minX: continuousSpaceStartX,
        maxX: currentX,
      })
      continuousSpaceStartX = itemStart
      currentX = itemEnd
    }
  }

  // Add the final continuous space if one exists
  if (continuousSpaceStartX !== null && currentX !== null) {
    countertopSpaces.push({
      width: currentX - continuousSpaceStartX,
      minX: continuousSpaceStartX,
      maxX: currentX,
    })
  }

  return countertopSpaces
}

/**
 * Helper function to find the closest valid position for a countertop item
 */
export const findClosestValidPosition = (
  $item: { id: string; x: number; $width: number },
  countertopSpaces: Array<{ width: number; minX: number; maxX: number }>,
  allCountertopItems: Array<{ id: string; x: number; $width: number }>
): number | null => {
  const itemWidth = $item.$width
  const currentX = $item.x

  // Filter out the item we're trying to place
  const otherItems = allCountertopItems.filter(item => item.id !== $item.id)

  let bestPosition: number | null = null
  let minDistance = Infinity

  // Try each countertop space
  for (const space of countertopSpaces) {
    const minX = space.minX
    const maxX = space.maxX - itemWidth

    if (minX > maxX) {
      // Space is too small for this item
      continue
    }

    // Try to find positions in this space
    let currentTestX = minX

    while (currentTestX <= maxX) {
      const itemStart = currentTestX
      const itemEnd = currentTestX + itemWidth

      // Check if this position conflicts with any existing items
      const hasConflict = otherItems.some(existingItem => {
        const existingStart = existingItem.x
        const existingEnd = existingItem.x + existingItem.$width

        // Check for overlap
        return !(itemEnd <= existingStart || itemStart >= existingEnd)
      })

      if (!hasConflict) {
        // Valid position found - check if it's closer than the best we've found
        const distance = Math.abs(currentTestX - currentX)

        if (distance < minDistance) {
          minDistance = distance
          bestPosition = currentTestX
        }

        // If we found the exact same position, we're done
        if (distance === 0) {
          return bestPosition
        }
      }

      // Move to the next potential position (try after the conflicting item)
      const conflictingItem = otherItems.find(existingItem => {
        const existingStart = existingItem.x
        const existingEnd = existingItem.x + existingItem.$width
        return !(itemEnd <= existingStart || itemStart >= existingEnd)
      })

      if (conflictingItem) {
        // Move past the conflicting item
        currentTestX = conflictingItem.x + conflictingItem.$width
      } else {
        // No conflict, try next increment (we can be more granular here)
        currentTestX += 1
      }
    }
  }

  return bestPosition
}

/**
 * Generate updates to reposition invalid countertop items based on new bottom item positions
 *
 * @param wallId - The wall ID to check countertop items for
 * @param newBottomItemPositions - Array of bottom items with their new positions and properties
 * @param domainContext - The domain context to read entities
 * @param stackProjections - Stack projections for getting component instances
 * @param committedState - The state to read entities from
 * @returns Array of component modification updates for invalid countertop items
 */
export const generateCountertopRepositioningUpdates = (
  $wall: EnhancedDomain.$Wall,
  newBottomItemPositions: Array<{ x: number; $width: number; $hasWorkSurface: boolean }>,
  stackProjections: StackProjections<any, any, any, any, any, any>,
  committedState: DomainState<Model.AnyDomainEntity>
): Array<ReturnType<typeof configuratorInterface.updates.ComponentModification>> => {
  // Calculate the new continuous countertop spaces based on new bottom item positions
  const countertopSpaces = calculateContinuousCountertopSpaces(newBottomItemPositions)

  // Get all countertop items on this wall
  const countertopItems = $wall.$countertopItems.map($item => ({
    id: $item.id,
    x: $item.x,
    $width: $item.$width,
  }))

  const repositionedCountertopItems: Array<ReturnType<typeof configuratorInterface.updates.ComponentModification>> = []

  // Check each countertop item
  for (const item of countertopItems) {
    const itemStart = item.x
    const itemEnd = item.x + item.$width

    // Check if item is in a valid position
    const isValid = countertopSpaces.some(space => itemStart >= space.minX && itemEnd <= space.maxX)

    if (!isValid) {
      // Find the closest valid position
      const newPosition = findClosestValidPosition(item, countertopSpaces, countertopItems)

      if (newPosition !== null) {
        const $countertopItem = $wall.$countertopItems.find($i => $i.id === item.id)!
        const countertopStackItem = stackProjections.getStackItem($countertopItem.id, committedState)

        repositionedCountertopItems.push(
          configuratorInterface.updates.ComponentModification({
            componentInstance: countertopStackItem.componentInstance,
            updateTag: 'countertopRepositioning',
            writableState: {
              x: newPosition,
              stackKey: getCountertopWallStackId($wall.id),
            },
          })
        )
      }
    }
  }

  return repositionedCountertopItems
}
